import { onBeforeUnmount, nextTick } from 'vue';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

/**
 * Composable for managing Tippy.js tooltips
 * @returns {Object} Tooltip utilities
 */
export function useTooltip() {
  const tooltipInstances = [];

  /**
   * Create a tooltip for a single element
   * @param {Element|string} element - DOM element or selector
   * @param {string|Object} options - Tooltip content or full options object
   * @returns {Object} Tippy instance
   */
  const createTooltip = (element, options = {}) => {
    // Handle both simple string content and full options object
    const config = typeof options === 'string' 
      ? { content: options }
      : options;

    // Default configuration
    const defaultConfig = {
      placement: 'top',
      theme: 'dark',
      arrow: true,
      animation: 'scale',
      duration: [200, 150],
      hideOnClick: true,
      trigger: 'mouseenter focus',
    };

    // Merge configs
    const finalConfig = { ...defaultConfig, ...config };

    // Create tooltip instance
    const instance = tippy(element, finalConfig);
    tooltipInstances.push(instance);
    
    return instance;
  };

  /**
   * Create tooltips for multiple elements at once
   * @param {Array} configs - Array of {element, content, ...options} objects
   */
  const createTooltips = (configs) => {
    configs.forEach(config => {
      const { element, content, ...options } = config;
      if (element) {
        createTooltip(element, { content, ...options });
      }
    });
  };

  /**
   * Initialize tooltips from data attributes in the DOM
   * @param {Element|string} container - Container element or selector to search within
   */
  const initFromDataAttributes = async (container = document) => {
    await nextTick();
    
    const containerEl = typeof container === 'string' 
      ? document.querySelector(container) 
      : container;
    
    if (!containerEl) return;

    // Find all elements with data-tooltip attribute
    const elements = containerEl.querySelectorAll('[data-tooltip]');
    
    elements.forEach(el => {
      const content = el.getAttribute('data-tooltip');
      const placement = el.getAttribute('data-tooltip-placement') || 'top';
      
      // Check for position classes
      let finalPlacement = placement;
      if (el.classList.contains('tooltip-top')) finalPlacement = 'top';
      else if (el.classList.contains('tooltip-bottom')) finalPlacement = 'bottom';
      else if (el.classList.contains('tooltip-left')) finalPlacement = 'left';
      else if (el.classList.contains('tooltip-right')) finalPlacement = 'right';
      else if (el.classList.contains('tooltip-top-left')) finalPlacement = 'top-start';
      else if (el.classList.contains('tooltip-top-right')) finalPlacement = 'top-end';
      else if (el.classList.contains('tooltip-bottom-left')) finalPlacement = 'bottom-start';
      else if (el.classList.contains('tooltip-bottom-right')) finalPlacement = 'bottom-end';
      
      createTooltip(el, {
        content,
        placement: finalPlacement,
      });
      
      // Remove data-tooltip to prevent CSS tooltip from showing
      el.removeAttribute('data-tooltip');
    });
  };

  /**
   * Destroy all tooltip instances
   */
  const destroyAll = () => {
    tooltipInstances.forEach(instance => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
    tooltipInstances.length = 0;
  };

  /**
   * Update tooltip content
   * @param {Object} instance - Tippy instance
   * @param {string} newContent - New content for the tooltip
   */
  const updateContent = (instance, newContent) => {
    if (instance && instance.setContent) {
      instance.setContent(newContent);
    }
  };

  // Clean up on component unmount
  onBeforeUnmount(() => {
    destroyAll();
  });

  return {
    createTooltip,
    createTooltips,
    initFromDataAttributes,
    destroyAll,
    updateContent,
  };
}